import NotificationAlert from '@Components/NotificationAlert/NotificationAlert';
import AlertServices from '@Services/Alert.services';
import { Alert } from '@Types/Alert.types';
import { PaginatedResponse } from '@Types/Response.types';
import { PAGE_SIZE, PAGINATED_DEFAULT, PATHS } from '@Utils/constants';
import { handleRequestError } from '@Utils/request.utils';
import { Button, ErrorDisplay, FlyoutModal, LogoLoader, Pagination } from '@kg-internal/pv3-uikit';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import './NotificationsModal.scss';

type Props = {
  show: boolean;
  onClose: () => void;
  setNumberOfAlerts: (numberOfAlerts: string) => void;
  setShow: (show: boolean) => void;
};
const block = 'notification-modal';
const NotificationsModal = ({ show, onClose, setNumberOfAlerts, setShow }: Props) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [alerts, setAlerts] = useState<PaginatedResponse<Alert>>(PAGINATED_DEFAULT);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [currentPage, setCurrentPage] = useState<number>(1);

  const navigate = useNavigate();

  const handleNewPage = (newPage: number, data: PaginatedResponse<Alert>) => {
    if (newPage <= 0) {
      return;
    }
    const page = newPage > data.totalPages ? data.prevPage : newPage;
    setCurrentPage(page || 1);
  };

  const getAlerts = () => {
    setIsLoading(true);
    AlertServices.updateAlerts(currentPage, true, true)
      .then(({ data }) => {
        setAlerts(data);
        handleNewPage(currentPage, data);
        setNumberOfAlerts(data.totalDocs > 99 ? '99+' : data.totalDocs);
      })
      .catch(({ response }) => handleRequestError(response, setErrorMessage))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    getAlerts();
  }, [currentPage]);

  const handleNotificationClick = (id: string) => {
    setShow(false);
    setIsLoading(true);
    AlertServices.viewAlert(id)
      .catch(({ response }) => {
        handleRequestError(response, setErrorMessage);
      })
      .finally(() => setIsLoading(false));
  };

  const handleNotificationDismiss = (id: string) => {
    setIsLoading(true);
    AlertServices.hideAlert(id)
      .then(() => {
        getAlerts();
      })
      .catch(({ response }) => handleRequestError(response, setErrorMessage))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (!show) {
      setCurrentPage(1);
    }
  }, [show]);

  return (
    <FlyoutModal show={show} onClose={onClose}>
      <div className={`${block}__body`}>
        <FlyoutModal.Body>
          <h2>Notifications</h2>
          {isLoading && <LogoLoader inline />}

          {errorMessage ? (
            <ErrorDisplay inline details={errorMessage} />
          ) : (
            !isLoading && (
              <>
                <Button
                  type='primary'
                  fullWidth
                  onClick={() => {
                    navigate(PATHS.NOTIFICATIONS);
                    onClose();
                  }}>
                  Show All Notifications
                </Button>
                <div className={`${block}__notifications`}>
                  {alerts.docs &&
                    alerts.docs.length > 0 &&
                    alerts.docs.map((alert: Alert) => (
                      <NotificationAlert
                        key={alert.id}
                        alert={alert}
                        onDismiss={handleNotificationDismiss}
                        onClick={handleNotificationClick}
                      />
                    ))}
                </div>
                <Pagination
                  count={alerts.totalDocs}
                  perPage={PAGE_SIZE}
                  currentPage={currentPage - 1}
                  onChange={(value) => handleNewPage(value + 1, alerts)}
                />
              </>
            )
          )}
        </FlyoutModal.Body>
      </div>
    </FlyoutModal>
  );
};

export default NotificationsModal;
